// src/components/EmailVerificationBanner.js
import { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../firebase/useAuth';
import { sendEmailVerification } from 'firebase/auth';
import { useToast } from '../../context/ToastContext';
import ActionButton from './ActionButton';
import GlassyIcon from './GlassyIcon';

const EmailVerificationBanner = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleResend = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await sendEmailVerification(user);
      showToast('A new verification email has been sent to your inbox.', 'success');
    } catch (error) {
      if (error.code === 'auth/too-many-requests') {
        showToast('Email was already sent recently. Please check your inbox and spam folder, or try again in a few minutes.', 'info');
      } else {
        showToast('Failed to send verification email. Please try again later.', 'error');
      }
      console.error('Error resending verification email:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative backdrop-blur-sm border border-white/20 bg-gradient-to-br from-amber-100/60 to-amber-50/40 dark:from-amber-900/40 dark:to-amber-800/20 p-4 w-full">
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
          <div className="flex items-start sm:items-center text-center sm:text-left">
            <div className="mr-3 mt-0.5 sm:mt-0 flex-shrink-0">
              <GlassyIcon icon={ExclamationTriangleIcon} size="sm" color="amber" />
            </div>
            <span className="font-medium leading-relaxed text-gray-900 dark:text-white drop-shadow-sm">
              Your email is not verified. Please check your inbox (or spam folder) to verify your account.
            </span>
          </div>
          <ActionButton
            onClick={handleResend}
            loading={isLoading}
            variant="glassy"
            color="amber"
            className="font-bold px-2 py-2"
          >
            Resend Email
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
